import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { Alert } from '../../components/ui'
import { fetchSettings } from '../../api/settings'
import { t } from '../../i18n'

/**
 * A line across the inbox when nothing can leave the account.
 *
 * The kill switch moves on its own — a revoked session, a long flood wait, the
 * day's send limit — and everything after that looks normal: the draft is
 * approved, the panel says the send is queued, and the job snoozes for a minute
 * and comes back for ever. Nothing on this screen said why the client never
 * answered.
 *
 * It reads the same endpoint the settings screen does and shows nothing at all
 * when sending is on, which is the ordinary state. A banner that is always there
 * is a banner nobody reads.
 */
export function SendingOff() {
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: ({ signal }) => fetchSettings(signal),
    // It changes without anybody pressing anything, so it is worth asking again
    // while the screen is open. A minute is far below the time it takes to write
    // a reply and well above anything that would bother the server.
    refetchInterval: 60_000,
  })

  if (settings.data === undefined || settings.data.outboundEnabled) return null

  return (
    <Alert tone="error">
      <div>
        <strong>{t('outbound.bannerTitle')}</strong>
        <div>
          {t(`outbound.by.${settings.data.outboundSource}` as 'outbound.by.person', {
            reason: settings.data.outboundReason === ''
              ? t('outbound.noReason')
              : settings.data.outboundReason,
          })}
        </div>
        <div className="sending-off__link">
          <Link to="/settings">{t('outbound.bannerLink')}</Link>
        </div>
      </div>
    </Alert>
  )
}
